import React, { useEffect, useMemo, useState } from "react"
import styled from "styled-components"
import Box from "../atoms/Box"
import Image from "../utils/Image"
import Link from "../utils/Link"

const StyledOverlayImage = styled(Image)`
  opacity: 0;
  transition: opacity 0.3s ease;
  will-change: opacity;

  &:hover {
    opacity: 1;
  }
`

const StyledLink = styled(Link)`
  &:hover {
    & .image {
      ${props => (!props.overlayImage ? "transform: scale(1.05)" : null)};
    }
  }
`

const CascadeGrid = ({ children, flexGrid, maxWidth, cascadeGutter }) => {
  // const columnCountObj = constructColumnCountObj(maxWidth, flexGrid.columns)

  // Normalize children to an array
  const items = useMemo(
    () => (Array.isArray(children) ? children : children ? [children] : []),
    [children],
  )

  /** Convert Strapi slider values (numbers) or strings into CSS lengths. Examples: 16 -> "16px" */
  const toCssLen = v =>
    typeof v === "number" ? `${v}px` : (v || "").toString().trim()

  // Supply the same gap across breakpoints so the Strapi slider applies uniformly.
  const gutterBreakPoints = useMemo(() => {
    const val = toCssLen(
      typeof cascadeGutter === "number" || typeof cascadeGutter === "string"
        ? cascadeGutter
        : 16, // default gap if nothing provided
    )
    return { 350: val, 750: val, 1024: val }
  }, [cascadeGutter])

  /**
   *  Prevent hydration mismatches only mount the masonry layout on the client
   *  For SSR, fallback to render a simple vertical stack with margin-bottom equal to the gap.
   */
  const normalizedServerGutter = useMemo(() => {
    if (
      typeof cascadeGutter === "number" ||
      (typeof cascadeGutter === "string" && cascadeGutter.trim() !== "")
    ) {
      return toCssLen(cascadeGutter)
    }
    return "16px"
  }, [cascadeGutter])

  // Defer Masonry to client to avoid hydration mismatch and SSR build errors.
  const [masonryComponents, setMasonryComponents] = useState(null)
  useEffect(() => {
    let mounted = true

    import("react-responsive-masonry")
      .then(module => {
        if (!mounted) return

        setMasonryComponents({
          Masonry: module.default,
          ResponsiveMasonry: module.ResponsiveMasonry,
        })
      })
      .catch(() => {
        if (!mounted) return
        setMasonryComponents(null)
      })

    return () => {
      mounted = false
    }
  }, [])

  // Generate stable keys for child items: Prefer ids/hashes if present. fall back to a deterministic index-based key
  const getItemKey = (child, i) =>
    child?.id ??
    child?.strapi_id ??
    child?.image?.id ??
    child?.image?.hash ??
    child?.image?.url ??
    child?.title ??
    `cascade-item-${i}`

  const renderItem = (child, i) => {
    const key = getItemKey(child, i)
    return (
      <StyledLink
        key={key}
        optional
        overlayImage={child?.overlayImage}
        {...(child?.link || {})}
      >
        <Box position="relative" style={{ borderRadius: "5px", width: "100%" }}>
          {/* width: 100% helps Masonry calculate item widths correctly */}
          <Image
            className="image"
            alternativeText="Image Tile in Cascade Grid Layout"
            {...(child?.image || {})}
          />
          {child?.overlayImage && (
            <Box
              transitionProperty="height, transform"
              transitionTimingFunction="ease"
              transitionDuration="md"
              height="100%"
            >
              <StyledOverlayImage
                {...child.overlayImage}
                style={{
                  width: "100%",
                  left: 0,
                  top: 0,
                  bottom: 0,
                  position: "absolute",
                  zIndex: 90,
                }}
              />
            </Box>
          )}
        </Box>
      </StyledLink>
    )
  }

  // SSR fallback: simple stack with vertical spacing to avoid hydration issues.
  if (!masonryComponents) {
    return (
      <>
        {items.map((child, i) => (
          <Box key={getItemKey(child, i)} mb={normalizedServerGutter}>
            {renderItem(child, i)}
          </Box>
        ))}
      </>
    )
  }

  const { Masonry, ResponsiveMasonry } = masonryComponents
  const columnsCountBreakPoints = { 350: 1, 750: 2, 1024: 3 }

  return (
    <>
      <ResponsiveMasonry
        columnsCountBreakPoints={columnsCountBreakPoints}
        gutterBreakPoints={gutterBreakPoints} // container controls both horizontal & vertical spacing
      >
        {/* Setting item gutter to 0 so the container "gap" is control by gutterBreakPoints */}
        <Masonry gutter="0">
          {items.map((child, i) => renderItem(child, i))}
        </Masonry>
      </ResponsiveMasonry>
    </>
  )
}

// TODO::: use maxWidth and columns to calculate columnsCountBreakPoints object
// FUNCTION IS NOT IMPLEMENTED OR COMPLETE
const constructColumnCountObj = (maxWidth, columns) => {
  const gatsbyBreakPoints = [425, 640, 768, 1024, 1280]
  let breakPoints = {}

  // get max columns
  let cols = []
  Object.keys(columns).map(bp => {
    cols.push(columns[bp])
  })

  const maxCol = Math.max(...cols)
  for (let i = 1; i <= maxCol; i++) {
    if (maxWidth > gatsbyBreakPoints[i - 1]) {
      // all below gatsby breakpoint
      breakPoints[gatsbyBreakPoints[i - 1]] = i
    } else if (maxWidth <= gatsbyBreakPoints[i - 1] && i === maxCol) {
      breakPoints[parseInt(maxWidth)] = i
    }
  }

  return breakPoints
}

export default CascadeGrid
